import { Box, IconButton } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { RichTextEditor, RichTextReadOnly } from '@/components/common/RichTextEditor';
import type { ReadOnlyVariant } from '@/auth/sectionReadOnlyStyle';

interface DevelopmentNeedError {
  developmentNeed: boolean;
  actionToResolve: boolean;
}

interface DevelopmentNeedRowProps {
  developmentNeed: string;
  actionToResolve: string;
  onChange: (field: 'developmentNeed' | 'actionToResolve', value: string) => void;
  onBlur?: () => void;
  onDelete: () => void;
  canEdit: boolean;
  canDelete: boolean;
  error: DevelopmentNeedError;
  readOnlyVariant?: ReadOnlyVariant;
  striped?: boolean;
}

export function DevelopmentNeedRow({
  developmentNeed,
  actionToResolve,
  onChange,
  onBlur,
  onDelete,
  canEdit,
  canDelete,
  error,
  readOnlyVariant = 'boxed',
  striped = false,
}: DevelopmentNeedRowProps) {
  return (
    <Box
      data-testid="development-need-row"
      data-striped={striped || undefined}
      sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}
    >
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          gap: 2,
          p: 1.5,
          borderRadius: 1,
          ...(striped && { bgcolor: 'custom.tableRowStripe' }),
        }}
      >
        <Box
          sx={{
            flex: 1,
            ...(error.developmentNeed && {
              border: '2px solid',
              borderColor: 'error.main',
              borderRadius: 1,
            }),
          }}
          data-testid={error.developmentNeed ? 'field-error' : undefined}
        >
          {canEdit ? (
            <RichTextEditor
              content={developmentNeed}
              onChange={(html) => onChange('developmentNeed', html)}
              onBlur={onBlur}
              placeholder="Enter development need..."
            />
          ) : (
            <RichTextReadOnly content={developmentNeed} variant={readOnlyVariant} />
          )}
        </Box>
        <Box
          sx={{
            flex: 1,
            ...(error.actionToResolve && {
              border: '2px solid',
              borderColor: 'error.main',
              borderRadius: 1,
            }),
          }}
          data-testid={error.actionToResolve ? 'field-error' : undefined}
        >
          {canEdit ? (
            <RichTextEditor
              content={actionToResolve}
              onChange={(html) => onChange('actionToResolve', html)}
              onBlur={onBlur}
              placeholder="Enter action to resolve..."
            />
          ) : (
            <RichTextReadOnly content={actionToResolve} variant={readOnlyVariant} />
          )}
        </Box>
      </Box>
      {canEdit && (
        <IconButton
          // Prevent the click from blurring a focused editor: the blur would
          // fire its own save with the same rowVersion and race the delete → 409.
          onMouseDown={(e) => e.preventDefault()}
          onClick={onDelete}
          color="error"
          size="small"
          disabled={!canDelete}
          aria-label="Delete development need"
        >
          <DeleteOutlineIcon />
        </IconButton>
      )}
    </Box>
  );
}

export default DevelopmentNeedRow;
